import {
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppointmentModal from '../components/AppointmentModal';
import CustomDataGrid from '../components/CustomDataGrid';
import Loading from '../components/Loading';
import { useDietitianData } from '../hooks/useDietitianData';
import { Appointment, appointmentApi } from '../services/appointmentApi';
import { colors } from '../theme';

const DietitianAppointmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    dietitianId,
    clients,
    appointments,
    loading,
    setAppointments,
    showSuccess,
    showError,
  } = useDietitianData({
    fetchClients: true,
    fetchAppointments: true,
    fetchDietPrograms: false,
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    {}
  );
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);

  const isMobile = useMediaQuery('(max-width: 600px)');

  const handleAddAppointment = async (appointmentData: any) => {
    try {
      const response = await appointmentApi.createAppointment(appointmentData);
      setAppointments((prev) => [...prev, response.appointment]);
      showSuccess('Randevu başarıyla oluşturuldu!', 'Randevu Eklendi');
      setIsAppointmentModalOpen(false);
    } catch (err) {
      showError(
        'Randevu eklenirken bir sorun oluştu. Lütfen tekrar deneyin.',
        'Ekleme Hatası'
      );
      console.error('Error creating appointment:', err);
    }
  };

  // Filtrelenmiş randevular
  const filteredAppointments = appointments.filter((apt) => {
    const query = searchQuery.toLowerCase();
    const matchesSearch =
      !searchQuery ||
      apt.clientName.toLowerCase().includes(query) ||
      apt.type?.toLowerCase().includes(query) ||
      apt.status?.toLowerCase().includes(query) ||
      apt.location?.toLowerCase().includes(query);

    if (!matchesSearch) {
      return false;
    }

    // Column filters
    for (const [field, filterValue] of Object.entries(columnFilters)) {
      if (!filterValue) continue;

      let value = '';
      if (field === 'time') {
        value = `${apt.startTime} - ${apt.endTime}`.toLowerCase();
      } else {
        value = String(apt[field as keyof Appointment] || '').toLowerCase();
      }

      if (!value.includes(filterValue.toLowerCase())) {
        return false;
      }
    }

    return true;
  });

  const handleColumnFilter = (field: string, value: string) => {
    setColumnFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // DataGrid columns
  const columns: GridColDef[] = [
    {
      field: 'clientName',
      headerName: 'Danışan',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<Appointment>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: colors.primary.main,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '1.1rem',
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(46, 125, 50, 0.3)',
            }}
          >
            {params.value?.charAt(0) || 'D'}
          </Box>
          <Typography
            variant="body1"
            fontWeight={600}
            fontSize="1rem"
            sx={{ color: colors.primary.dark }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'appointmentDate',
      headerName: 'Tarih',
      flex: 1,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams<Appointment>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CalendarIcon sx={{ fontSize: 20, color: colors.text.secondary }} />
          <Typography variant="body2" fontSize="0.95rem">
            {params.value
              ? new Date(params.value).toLocaleDateString('tr-TR')
              : '-'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'time',
      headerName: 'Saat',
      flex: 1,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams<Appointment>) => (
        <Typography variant="body2" fontSize="0.95rem">
          {params.row.startTime} - {params.row.endTime}
        </Typography>
      ),
    },
    {
      field: 'type',
      headerName: 'Tür',
      flex: 1,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams<Appointment>) =>
        params.value ? (
          <Chip
            label={params.value}
            sx={{
              bgcolor: colors.background.light,
              fontWeight: 600,
              fontSize: '0.9rem',
              height: 36,
              px: 1,
            }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        ),
    },
    {
      field: 'status',
      headerName: 'Durum',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<Appointment>) =>
        params.value ? (
          <Chip
            label={params.value}
            size="small"
            sx={{
              bgcolor:
                params.value === 'Planlandı'
                  ? colors.primary.main
                  : params.value === 'Tamamlandı'
                  ? colors.primary.dark
                  : params.value === 'İptal Edildi'
                  ? colors.status.error
                  : colors.text.secondary,
              color: 'white',
              fontWeight: 600,
              fontSize: '0.9rem',
              height: 36,
              px: 1,
            }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        ),
    },
    {
      field: 'location',
      headerName: 'Konum',
      flex: 1,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams<Appointment>) => (
        <Typography variant="body2" color="text.secondary" fontSize="0.95rem">
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'fee',
      headerName: 'Ücret',
      width: 100,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params: GridRenderCellParams<Appointment>) => (
        <Typography variant="body2" fontSize="0.95rem" fontWeight={600}>
          {params.value ? `₺${params.value}` : '-'}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Oluşturulma',
      flex: 1,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams<Appointment>) => (
        <Typography variant="body2" color="text.secondary" fontSize="0.95rem">
          {params.value
            ? new Date(params.value).toLocaleDateString('tr-TR')
            : '-'}
        </Typography>
      ),
    },
  ];

  const handleRowClick = (params: any) => {
    if (params.row.id) {
      navigate(`/dietitian/appointments/${params.row.id}`);
    }
  };

  if (loading) {
    return (
      <Loading
        size="large"
        variant="wave"
        mode="inline"
        message="Randevular yükleniyor..."
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          height: 'calc(100vh - 24px)',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '100vw',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            bgcolor: 'white',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            mb: 3,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              flex={1}
              minWidth={0}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: colors.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <CalendarIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box minWidth={0} flex={1}>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: colors.text.primary }}
                  noWrap
                >
                  Randevularım
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {filteredAppointments.length} / {appointments.length} randevu
                </Typography>
              </Box>
            </Box>
            {!isMobile && (
              <Button
                variant="contained"
                onClick={() => setIsAppointmentModalOpen(true)}
                sx={{
                  bgcolor: colors.primary.main,
                  color: 'white',
                  px: 3,
                  py: 1.5,
                  width: 300,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(46, 125, 50, 0.3)',
                  ml: 2,
                  flexShrink: 0,
                  '&:hover': {
                    bgcolor: colors.hover.primary,
                    boxShadow: '0 6px 16px rgba(46, 125, 50, 0.4)',
                  },
                }}
              >
                Yeni Randevu Ekle
              </Button>
            )}
            {isMobile && (
              <IconButton
                onClick={() => setIsAppointmentModalOpen(true)}
                sx={{
                  bgcolor: colors.primary.main,
                  color: 'white',
                  width: 36,
                  height: 36,
                  ml: 1,
                  flexShrink: 0,
                  '&:hover': {
                    bgcolor: colors.hover.primary,
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {/* Search Bar */}
          <TextField
            fullWidth
            placeholder="Randevu ara (danışan, tür, durum, konum...)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: colors.background.light,
                borderRadius: 2,
                '&:hover': {
                  bgcolor: 'white',
                },
                '&.Mui-focused': {
                  bgcolor: 'white',
                },
              },
            }}
          />
        </Box>

        {/* DataGrid */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <CustomDataGrid
            rows={filteredAppointments.sort(
              (a, b) =>
                new Date(a.appointmentDate).getTime() -
                new Date(b.appointmentDate).getTime()
            )}
            columns={columns}
            loading={loading}
            onRowClick={handleRowClick}
            onColumnFilter={handleColumnFilter}
            noRowsLabel="Henüz randevu bulunmuyor"
          />
        </Box>
      </Box>

      <AppointmentModal
        open={isAppointmentModalOpen}
        onClose={() => setIsAppointmentModalOpen(false)}
        onSave={handleAddAppointment}
        clients={clients as any}
        dietitianId={dietitianId}
      />
    </>
  );
};

export default DietitianAppointmentsPage;
